import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

interface Transfer {
  destination: string;
  price: number;
}

const popularTransfers: Transfer[] = [
  { destination: 'Lyon', price: 76.95 },
  { destination: 'Val Thorens', price: 323.95 },
  { destination: 'Les Deux Alpes', price: 362.95 },
  { destination: "Val d'Isère", price: 364.95 },
  { destination: 'Courchevel', price: 362.95 },
  { destination: 'Tignes', price: 388.95 },
  { destination: 'Méribel', price: 349.95 },
  { destination: "Alpe d'Huez", price: 334.95 },
  { destination: 'La Plagne', price: 328.95 },
  { destination: 'Les Menuires', price: 328.95 },
  { destination: 'Chamonix', price: 395.95 },
  { destination: 'Les Arcs', price: 366.95 },
  { destination: 'Grenoble', price: 218.95 },
  { destination: 'Les Arcs (1950 / 2000)', price: 380.95 },
  { destination: 'Lyon Perrache Train Station', price: 76.95 },
  { destination: 'Part Dieu Train Station Lyon', price: 76.95 },
  { destination: 'Valmorel', price: 336.95 },
  { destination: 'Annecy City Centre', price: 291.95 },
  { destination: 'Val Claret', price: 401.95 },
  { destination: 'Les Belleville', price: 395.95 }
];

export default function LyonAirportPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://connectotransfers.com';

  useSEO({
    title: 'Transfert Aéroport Lyon vers Stations de Ski | Alpine Transfer Premium',
    description: 'Transfert privé depuis l\'aéroport Lyon Saint-Exupéry (LYS) vers Courchevel, Chamonix, Val d\'Isère et toutes les stations des Alpes. Service premium 24/7.',
    keywords: 'transfert Lyon, navette Lyon aéroport, VTC Lyon Courchevel, transport Lyon stations ski',
    canonical: '/transfert-aeroport-lyon',
    ogTitle: 'Transfert Aéroport Lyon - Stations de Ski | Alpine Transfer',
    ogDescription: 'Service de transfert privé premium depuis Lyon vers les stations de ski des Alpes',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/transfert-aeroport-lyon#webpage`,
          url: `${siteUrl}/transfert-aeroport-lyon`,
          name: 'Transfert Aéroport Lyon - Service Premium',
          description: 'Service de transfert privé depuis Lyon Aéroport vers les stations de ski alpines',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [featuredRef, featuredInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [transfersRef, transfersInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [pickupRef, pickupInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-[75vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/0805f3b32d811a69703a3d12770ae482.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="inline-flex items-center gap-2 px-5 py-2 bg-alpine-gold/90 backdrop-blur-sm rounded-full mb-6"
          >
            <i className="ri-global-line text-night-blue text-xl"></i>
            <span className="font-inter font-bold text-night-blue text-sm">Service Mondial</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="font-playfair text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Explorez Lyon<br />
            <span className="text-alpine-gold">avec Connecto</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={heroInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="text-slate-100 font-inter text-xl lg:text-2xl max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            Transferts sans faille depuis Lyon Aéroport<br />
            <strong className="text-alpine-gold">où chaque voyage commence avec confort et confiance</strong>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/reservation"
              className="inline-flex items-center gap-3 px-10 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-lg hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line text-xl"></i>
              Réserver maintenant
            </Link>
            <a
              href="tel:+33605606006"
              className="inline-flex items-center gap-3 px-10 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-lg hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              +33 6 05 60 60 06
            </a>
          </motion.div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-user-heart-line', title: 'Des millions de personnes nous font confiance' },
              { icon: 'ri-customer-service-2-line', title: "Service d'assistance 24h/24 et 7j/7" },
              { icon: 'ri-shield-check-line', title: 'Personnalisé et sécurisé' }
            ].map((badge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 bg-night-blue rounded-2xl flex items-center justify-center mb-4">
                  <i className={`${badge.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="font-inter text-xl font-bold text-night-blue">
                  {badge.title}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR TRANSFERS */}
      <section ref={transfersRef} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={transfersInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Les transferts tendances vers<br />
              <span className="text-alpine-gold">Lyon Aéroport</span>
            </h2>
            <p className="text-slate-600 font-inter text-lg">
              Plus de 20 destinations disponibles avec tarifs fixes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularTransfers.map((transfer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={transfersInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.05 * index }}
                className="group bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl p-6 hover:border-alpine-gold hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-slate-500 font-inter text-xs mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>Lyon Aéroport</span>
                    </div>
                    <h3 className="font-inter text-night-blue font-bold text-xl mb-1 group-hover:text-alpine-gold transition-colors">
                      {transfer.destination}
                    </h3>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-alpine-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-alpine-gold/20 transition-colors">
                    <i className="ri-plane-line text-alpine-gold text-2xl"></i>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-500 font-inter text-xs mb-1">Prix à partir de</p>
                      <p className="font-playfair text-alpine-gold font-bold text-2xl">
                        €{transfer.price.toFixed(2)}
                      </p>
                    </div>
                    <Link
                      to="/reservation"
                      className="inline-flex items-center gap-2 px-5 py-3 bg-night-blue text-white rounded-xl font-inter font-medium text-sm hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
                    >
                      Réserver
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT LYON AIRPORT */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Lyon Aéroport
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <p className="text-slate-700 font-inter text-lg leading-relaxed mb-6">
              L'aéroport Lyon-Saint Exupéry est situé à 25 km de la troisième plus grande ville de France - Lyon. Il porte le nom du célèbre aviateur et écrivain du roman mondialement connu Le Petit Prince. En visitant Lyon, préparez-vous à une explosion de culture et de vie nocturne qui convient à tous les goûts et saveurs. Nous dirions que c'est le meilleur choix pour les bon vivants (personnes qui aiment profiter de la belle vie).
            </p>
            <p className="text-slate-700 font-inter text-lg leading-relaxed">
              En 2019, l'aéroport a accueilli plus de 11 millions de passagers. Parce que de nombreuses stations de ski françaises sont relativement proches de Lyon, l'aéroport de Lyon est le point d'arrivée de nombreux passagers. Beaucoup d'entre eux réservent un transfert aéroport pour un trajet sûr et confortable vers leurs destinations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* AIRPORT PICKUP */}
      <section ref={pickupRef} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pickupInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Prise en charge à l'aéroport
            </h2>
            <p className="text-slate-600 font-inter text-xl max-w-3xl mx-auto">
              Évitez la file d'attente des taxis grâce à une course pré-réservée à des tarifs compétitifs.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={pickupInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-playfair text-3xl font-bold text-night-blue mb-8">
                Ce que nous offrons :
              </h3>

              <div className="space-y-6">
                {[
                  { icon: 'ri-user-star-line', text: 'Chauffeurs locaux professionnels' },
                  { icon: 'ri-price-tag-line', text: 'Tarifs fixes et transparents' },
                  { icon: 'ri-flight-takeoff-line', text: 'Suivi des vols pour des prises en charge ponctuelles' },
                  { icon: 'ri-customer-service-2-line', text: 'Assistance 24/7 et annulations simples' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-alpine-gold/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-alpine-gold text-2xl`}></i>
                    </div>
                    <p className="text-slate-700 font-inter text-lg pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={pickupInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-2 gap-6"
            >
              {[
                { icon: 'ri-price-tag-3-line', title: 'Tarifs tout compris' },
                { icon: 'ri-bank-card-line', title: 'Options de paiement flexibles' },
                { icon: 'ri-time-line', title: "Temps d'attente gratuit" },
                { icon: 'ri-car-line', title: 'Sélection variée de véhicules' }
              ].map((benefit, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-all">
                  <div className="w-16 h-16 bg-night-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-night-blue text-3xl`}></i>
                  </div>
                  <h4 className="font-inter text-base font-bold text-night-blue leading-snug">
                    {benefit.title}
                  </h4>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-6">
              Découvrez tous les aéroports<br />
              <span className="text-alpine-gold">à disposition de Connecto</span>
            </h2>
            <p className="text-slate-300 font-inter text-xl mb-10">
              Présent dans le monde entier • Confirmation immédiate • Assistance 24/7
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-3 px-12 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-xl hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-2xl"></i>
                Réserver maintenant
              </Link>
              <a
                href="tel:+33605606006"
                className="inline-flex items-center gap-3 px-12 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-xl hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-2xl"></i>
                Nous contacter
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-slate-300">
              <div className="flex items-center gap-2">
                <i className="ri-whatsapp-line text-green-400 text-2xl"></i>
                <a href="https://wa.me/33605606006" target="_blank" rel="noopener noreferrer" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  WhatsApp disponible
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-alpine-gold text-2xl"></i>
                <a href="mailto:contac.driver@outlook.fr" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  contac.driver@outlook.fr
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
